import { Link } from "react-router-dom";
import { Linkedin, Instagram } from "lucide-react";

const XIcon = ({ size = 24, className }: { size?: number; className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden="true"
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const Column = ({
  title,
  items,
}: {
  title: string;
  items: { to: string; label: string }[];
}) => (
  <div>
    <h3 className="font-mono text-[11px] font-medium tracking-[0.15em] uppercase text-[hsl(var(--fg-muted))]">
      {title}
    </h3>
    <ul className="mt-6 space-y-3">
      {items.map(({ to, label }) => (
        <li key={to}>
          <Link
            to={to}
            className="text-[14px] text-[hsl(var(--fg-cream))]/80 hover:text-[hsl(var(--accent-brick))] transition-colors duration-150"
          >
            {label}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

const Footer = () => {
  const year = new Date().getFullYear();

  const capabilities = [
    { to: "/future-customer-experience", label: "Future customer experience" },
    { to: "/what-we-do", label: "What we do" },
    { to: "/ai-agent-development", label: "Agent and automation build" },
    { to: "/how-we-work", label: "How we work" },
    { to: "/ai-employees", label: "AI Employees" },
    { to: "/work", label: "Work" },
    { to: "/private-equity", label: "Private equity" },
  ];

  const company = [
    { to: "/about", label: "About" },
    { to: "/careers", label: "Careers" },
    { to: "/privacy", label: "Privacy" },
    { to: "/terms", label: "Terms" },
  ];

  const resources = [
    { to: "/live", label: "Brick Live — AI training" },
    { to: "/compliance/eu-ai-act", label: "EU AI Act" },
    { to: "/compliance/eu-ai-act/deep-dive", label: "EU AI Act deep dive" },
    { to: "/ai-dictionary", label: "AI dictionary" },
    { to: "/faq", label: "FAQ" },
  ];

  const socials = [
    { href: "https://www.linkedin.com/company/brickplease/", icon: Linkedin, label: "LinkedIn" },
    { href: "https://x.com/brickplease", icon: XIcon, label: "X" },
    { href: "https://www.instagram.com/brick.please/", icon: Instagram, label: "Instagram" },
  ];

  return (
    <footer className="relative border-t border-[hsl(var(--border-hairline))] bg-[hsl(var(--bg-canvas))] text-[hsl(var(--fg-cream))]">
      <div className="max-w-6xl mx-auto px-6 pt-24 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 pb-16 border-b border-[hsl(var(--border-hairline))]">
          <div className="lg:col-span-5">
            <Link
              to="/"
              className="inline-block"
              aria-label="Brick — home"
              style={{
                fontFamily: "'Switzer-Variable', 'Switzer', ui-sans-serif, system-ui, sans-serif",
                fontWeight: 900,
                fontSize: "1.75rem",
                letterSpacing: "-0.03em",
                lineHeight: 1,
                color: "hsl(var(--fg-cream))",
              }}
            >
              BRICK<span style={{ color: "hsl(var(--accent-brick))" }}>.</span>
            </Link>
            <p className="mt-5 max-w-md text-[18px] leading-relaxed text-[hsl(var(--fg-cream))]/72">
              Design the business your customers will expect next.
            </p>
            <p className="mt-3 max-w-sm text-[13px] font-mono uppercase tracking-[0.08em] text-[hsl(var(--fg-muted))]">
              AI-native business design + build
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-10 md:gap-16 pt-16">
          <Column title="Capabilities" items={capabilities} />
          <Column title="Company" items={company} />
          <Column title="Resources" items={resources} />
        </div>

        <div className="mt-20 pt-10 border-t border-[hsl(var(--border-hairline))] grid grid-cols-1 md:grid-cols-3 gap-10">
          <div>
            <h4 className="font-mono text-[11px] font-medium tracking-[0.15em] uppercase text-[hsl(var(--fg-muted))]">
              Email
            </h4>
            <a
              href="mailto:erik@brickplease.com"
              className="mt-3 inline-block text-[15px] hover:text-[hsl(var(--accent-brick))] transition-colors duration-150"
            >
              erik@brickplease.com
            </a>
          </div>
          <div>
            <h4 className="font-mono text-[11px] font-medium tracking-[0.15em] uppercase text-[hsl(var(--fg-muted))]">
              Phone
            </h4>
            <a
              href="tel:+46735079079"
              className="mt-3 inline-block text-[15px] hover:text-[hsl(var(--accent-brick))] transition-colors duration-150"
            >
              +46 735 079 079
            </a>
          </div>
          <div>
            <h4 className="font-mono text-[11px] font-medium tracking-[0.15em] uppercase text-[hsl(var(--fg-muted))]">
              Address
            </h4>
            <p className="mt-3 text-[15px] leading-snug">
              BrickPlease AI Enablement AB
              <br />
              Odlingsgatan 7c
              <br />
              174 53 Sundbyberg, Sweden
              <br />
              <span className="font-mono text-[11px] tracking-[0.1em] uppercase text-[hsl(var(--fg-muted))]">
                Reg. no. 559587-5872
              </span>
            </p>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-[hsl(var(--border-hairline))] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <p className="font-mono text-[11px] tracking-[0.1em] uppercase text-[hsl(var(--fg-muted))]">
            © {year} BrickPlease AI Enablement AB. Build with them. Transfer ownership. Leave.
          </p>
          <div className="flex items-center gap-3">
            {socials.map(({ href, icon: Icon, label }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                className="w-9 h-9 flex items-center justify-center border border-[hsl(var(--border-hairline))] text-[hsl(var(--fg-cream))]/70 hover:text-[hsl(var(--accent-brick))] hover:border-[hsl(var(--accent-brick))] transition-colors duration-150"
              >
                <Icon className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
